/** Property serialization + PB-PROPS Tier 3 codec (encode/decode cart line properties). */

import {
  catalogDesignLibraryToTier1Props,
  decodeCatalogDesignLibraryFromProps,
  DESIGN_FILE_KEY,
  DESIGN_SOURCE_CATALOG,
  DESIGN_SOURCE_KEY,
  DESIGN_SOURCE_SCRATCH,
  CART_KEY_ENTRY_ID,
  CART_KEY_PREVIEW_URL,
  CART_KEY_PRODUCTION_URL,
  CART_KEY_MATCH,
  DEFAULT_NEW_DESIGN_LABEL,
} from '@theme/pb-design-library-snapshot';
import { lookupSelectionValue } from '@theme/pb-builder-tokens';
import { collectAiImageAllowedKeys, isDisplayableImageUrl } from '@theme/pb-cart-display-image';
import { mergeAiImageFieldIntoSelections } from '@theme/pb-dl-ai-hydrate';
import {
  collectAiTextAllowedKeys,
  encodeAiTextFieldProps,
  parseAiTextFieldSettings,
  resolveAiTextOutputPropertyKey,
} from '@theme/pb-ai-text-logic';
import {
  collectDerivedAllowedKeys,
  hydrateLegacyNameFromInitialSlots,
} from '@theme/pb-derived-properties-logic';

export { collectAiImageAllowedKeys };
export { collectAiTextAllowedKeys };

export const CART_SERIALIZE_MODE = 'numbered';
export { DESIGN_FILE_KEY, DESIGN_SOURCE_KEY, DESIGN_SOURCE_CATALOG, DESIGN_SOURCE_SCRATCH };

const HIDDEN_JSON_SUFFIX = '_json';
const DESIGN_HIDDEN_KEYS = [
  CART_KEY_ENTRY_ID,
  CART_KEY_PREVIEW_URL,
  CART_KEY_PRODUCTION_URL,
  CART_KEY_MATCH,
];
const AI_ERROR_KEY = 'AI Error';

/** Internal / placeholder strings that must never appear as customer-facing Tier-1 values. */
const JUNK_TIER1_VALUE_PATTERNS = [/^ai\s+error$/i, /^something went wrong/i];

function escapeRegExp(str) {
  return String(str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function toCleanArray(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map(function(item) { return String(item == null ? '' : item).trim(); })
    .filter(Boolean);
}

function parseNumberedKey(rawKey) {
  var key = String(rawKey || '');
  var match = key.match(/^(.*)\s\((\d+)\)\s*$/);
  if (!match) return null;
  return {
    baseKey: String(match[1] || '').trim(),
    index: parseInt(match[2], 10),
  };
}

function parseHiddenJsonKey(rawKey) {
  var key = String(rawKey || '');
  var match = key.match(/^_(.+)_json$/);
  if (!match) return null;
  return String(match[1] || '').trim();
}

function readHiddenArray(props, baseKey) {
  if (!props || typeof props !== 'object' || !baseKey) return [];
  var hiddenKey = '_' + baseKey + '_json';
  var raw = props[hiddenKey];
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    var parsed = JSON.parse(raw);
    return toCleanArray(parsed);
  } catch (e) {
    return [];
  }
}

function readNumberedArray(props, baseKey) {
  if (!props || typeof props !== 'object' || !baseKey) return [];
  var rx = new RegExp('^' + escapeRegExp(baseKey) + '\\s\\((\\d+)\\)\\s*$');
  var collected = [];
  Object.keys(props).forEach(function(key) {
    var match = String(key || '').match(rx);
    if (!match) return;
    var idx = parseInt(match[1], 10);
    if (!idx || idx < 1) return;
    var raw = props[key];
    if (raw == null || raw === '') return;
    collected.push({
      idx: idx,
      value: String(raw).trim(),
    });
  });
  collected.sort(function(a, b) { return a.idx - b.idx; });
  return collected
    .map(function(item) { return item.value; })
    .filter(Boolean);
}

function dedupePreserveOrder(arr) {
  var out = [];
  var seen = {};
  arr.forEach(function(item) {
    var v = String(item || '').trim();
    if (!v || seen[v]) return;
    seen[v] = true;
    out.push(v);
  });
  return out;
}

function isSystemPropertyKey(key) {
  var k = String(key || '');
  if (!k || k === 'Title') return true;
  if (k === '_pb_json') return true;
  if (k === AI_ERROR_KEY) return true;
  if (parseHiddenJsonKey(k)) return true;
  if (DESIGN_HIDDEN_KEYS.indexOf(k) !== -1) return true;
  return false;
}

/**
 * Label-as-value junk — e.g. Photo key with value "Photo" (no real answer).
 * @param {string} key
 * @param {unknown} value
 */
export function isLabelAsValueTier1Property(key, value) {
  var k = String(key || '').trim();
  var v = String(value == null ? '' : value).trim();
  if (!k || !v) return false;
  return k.toLowerCase() === v.toLowerCase();
}

/** Photo / Character tier-1 keys (including field-scoped variants). */
export function isAiMediaTier1Key(key) {
  var k = String(key || '').trim().toLowerCase();
  if (!k) return false;
  if (k === 'photo' || k === 'character') return true;
  if (k.endsWith(' - photo') || k.endsWith(' - character')) return true;
  return false;
}

/**
 * Valid tier-1 Photo/Character value — real URL, not label-as-value junk.
 * Non-media keys always pass (caller filters by key family first).
 * @param {string} key
 * @param {unknown} value
 */
export function isValidAiMediaTier1Value(key, value) {
  if (!isAiMediaTier1Key(key)) return true;
  var v = String(value == null ? '' : value).trim();
  if (!v) return false;
  if (isLabelAsValueTier1Property(key, value)) return false;
  return isDisplayableImageUrl(v);
}

/**
 * Drop invalid Photo/Character tier-1 keys from a flat property map (flatten/encode purge).
 * @param {Record<string, unknown>} [props]
 */
export function purgeInvalidAiMediaTier1Keys(props) {
  var out = {};
  Object.keys(props || {}).forEach(function(key) {
    var val = props[key];
    if (isAiMediaTier1Key(key) && !isValidAiMediaTier1Value(key, val)) return;
    out[key] = val;
  });
  return out;
}

/**
 * Tier-1 props that must not render on cart/order lines (encode + display contract).
 * @param {string} key
 * @param {unknown} value
 */
export function isJunkVisibleTier1Property(key, value) {
  if (isSystemPropertyKey(key)) return true;
  var v = String(value == null ? '' : value).trim();
  if (!v) return true;
  if (isLabelAsValueTier1Property(key, value)) return true;
  for (var i = 0; i < JUNK_TIER1_VALUE_PATTERNS.length; i += 1) {
    if (JUNK_TIER1_VALUE_PATTERNS[i].test(v)) return true;
  }
  return false;
}

/**
 * Drop junk / internal keys from a flat Tier-1 property map.
 * @param {Record<string, unknown>} [props]
 */
export function filterVisibleTier1Properties(props) {
  var out = {};
  Object.keys(props || {}).forEach(function(key) {
    var val = props[key];
    if (isJunkVisibleTier1Property(key, val)) return;
    out[key] = val;
  });
  return out;
}

function encodeDesignLibraryPropsFromState(state, fieldConfig) {
  var newDesignLabels = {};
  if (Array.isArray(fieldConfig)) {
    fieldConfig.forEach(function(field) {
      if (!field || !field.label) return;
      var type = fieldTypeLower(field);
      if (!type.includes('design library') && !type.includes('designlibrary')) return;
      newDesignLabels[String(field.label)] = newDesignLabelFromFieldSettings(field.settings);
    });
  }
  return catalogDesignLibraryToTier1Props(
    state && state.catalogDesignLibrary,
    (state && state.selections) || {},
    { newDesignLabels: newDesignLabels }
  );
}

function newDesignLabelFromFieldSettings(settings) {
  if (settings == null) return DEFAULT_NEW_DESIGN_LABEL;
  if (typeof settings === 'object' && !Array.isArray(settings)) {
    if (settings.new_design_label) return String(settings.new_design_label).trim() || DEFAULT_NEW_DESIGN_LABEL;
    if (typeof settings.value === 'string' && settings.value.trim()) {
      return newDesignLabelFromFieldSettings(settings.value);
    }
  }
  if (typeof settings === 'string') {
    var match = settings.match(/new_design_label:\s*([^\n]+)/);
    if (match) return match[1].trim() || DEFAULT_NEW_DESIGN_LABEL;
  }
  return DEFAULT_NEW_DESIGN_LABEL;
}

function fieldTypeLower(field) {
  return String((field && field.type) || '').toLowerCase();
}

/** @param {Array<{ label?: string, type?: string }>} [fieldConfig] */
export function getDesignLibraryFieldLabels(fieldConfig) {
  if (!Array.isArray(fieldConfig)) return [];
  return fieldConfig
    .filter(function(field) {
      const type = fieldTypeLower(field);
      return field && field.label && (type.includes('design library') || type.includes('designlibrary'));
    })
    .map(function(field) {
      return String(field.label);
    });
}

/**
 * Remove design-library tier-1 keys from selections before generic field encode.
 * Field-label v2 props are emitted only from catalogDesignLibrary encode.
 * @param {Record<string, unknown>} [selections]
 * @param {Array<{ label?: string, type?: string }>} [fieldConfig]
 */
export function stripDesignLibraryTier1KeysFromSelections(selections, fieldConfig) {
  if (!selections || typeof selections !== 'object') return {};
  const out = { ...selections };
  getDesignLibraryFieldLabels(fieldConfig).forEach(function(label) {
    delete out[label];
  });
  delete out[DESIGN_FILE_KEY];
  delete out[DESIGN_SOURCE_KEY];
  return out;
}

/**
 * Keep only design-library draft entries that belong on this product line.
 * @param {{ byLabel?: Record<string, object> }} [catalogDesignLibrary]
 * @param {Array<{ label?: string, type?: string }>} [fieldConfig]
 */
export function scopeCatalogDesignLibraryForFields(catalogDesignLibrary, fieldConfig) {
  if (!catalogDesignLibrary || !catalogDesignLibrary.byLabel) return undefined;
  const allowed = new Set(getDesignLibraryFieldLabels(fieldConfig));
  if (!allowed.size) return undefined;

  const byLabel = {};
  Object.keys(catalogDesignLibrary.byLabel).forEach(function(label) {
    if (!allowed.has(label)) return;
    byLabel[label] = catalogDesignLibrary.byLabel[label];
  });

  return Object.keys(byLabel).length ? { byLabel: byLabel } : undefined;
}

/**
 * TopicMulti answers are stored under per-topic property keys (not the field label).
 * Include raw templates and token-resolved keys so Tier 1 encode keeps topic answers.
 * @param {Array<{ label?: string, type?: string, settings?: unknown }>} [fieldConfig]
 * @param {Record<string, unknown>} [selections]
 */
export function collectTopicMultiAllowedKeys(fieldConfig, selections) {
  const keys = new Set();
  if (!Array.isArray(fieldConfig)) return keys;

  const ctx = { selections: selections || {} };
  fieldConfig.forEach(function(field) {
    if (!field || !field.label || String(field.type || '').indexOf('TopicMulti') === -1) return;
    const parsed = parseTopicSettings(settingsToString(field.settings));
    parsed.topics.forEach(function(topic) {
      const trimmed = String(topic || '').trim();
      if (!trimmed) return;
      keys.add(trimmed);
      keys.add(audioPropertyLabel(trimmed));
      const resolved = resolvePersonalizationTokens(trimmed, ctx);
      if (resolved && resolved !== trimmed) {
        keys.add(resolved);
        keys.add(audioPropertyLabel(resolved));
      }
    });
  });
  return keys;
}

/**
 * Strip selections that do not belong to this product's personalization config.
 * @param {Record<string, unknown>} [selections]
 * @param {Array<{ label?: string, type?: string }>} [fieldConfig]
 * @param {Array<{ name?: string }>} [options]
 */
export function filterSelectionsForProductFields(selections, fieldConfig, options) {
  if (!selections || typeof selections !== 'object') return {};
  if (!Array.isArray(fieldConfig) || !fieldConfig.length) return { ...selections };

  const allowed = new Set();
  fieldConfig.forEach(function(field) {
    if (!field || !field.label) return;
    allowed.add(String(field.label));
    const type = fieldTypeLower(field);
    if (type.includes('long text') || type.includes('longtext') || type.includes('audio')) {
      allowed.add(String(field.label) + ' (audio)');
    }
  });

  collectTopicMultiAllowedKeys(fieldConfig, selections).forEach(function(key) {
    allowed.add(key);
  });

  collectAiImageAllowedKeys(fieldConfig).forEach(function(key) {
    allowed.add(key);
  });

  collectAiTextAllowedKeys(fieldConfig).forEach(function(key) {
    allowed.add(key);
  });

  collectDerivedAllowedKeys(fieldConfig).forEach(function(key) {
    allowed.add(key);
  });

  (options || []).forEach(function(opt) {
    if (opt && opt.name && opt.name !== 'Title') allowed.add(String(opt.name));
  });

  if (getDesignLibraryFieldLabels(fieldConfig).length) {
    getDesignLibraryFieldLabels(fieldConfig).forEach(function(label) {
      allowed.add(label);
    });
  }

  const out = {};
  Object.keys(selections).forEach(function(key) {
    if (!key || key.charAt(0) === '_') return;
    if (allowed.has(key)) {
      out[key] = selections[key];
      return;
    }
    const numbered = parseNumberedKey(key);
    if (numbered && allowed.has(numbered.baseKey)) {
      out[key] = selections[key];
    }
  });
  return out;
}

/**
 * @param {string} key
 * @param {unknown} value
 * @param {'hybrid'|'numbered'|'delimited'} [mode]
 */
export function serializePropertyValue(key, value, mode) {
  var out = {};
  var cleanKey = String(key || '').trim();
  var selectedMode = mode || 'hybrid';
  if (!cleanKey || value == null || value === '') return out;

  if (Array.isArray(value)) {
    var arr = toCleanArray(value);
    if (!arr.length) return out;
    if (arr.length === 1) {
      out[cleanKey] = arr[0];
      return out;
    }
    if (selectedMode === 'delimited') {
      out[cleanKey] = arr.join('|||');
      return out;
    }
    arr.forEach(function(item, idx) {
      out[cleanKey + ' (' + (idx + 1) + ')'] = item;
    });
    if (selectedMode === 'hybrid') {
      out['_' + cleanKey + '_json'] = JSON.stringify(arr);
    }
    return out;
  }

  out[cleanKey] = String(value);
  return out;
}

/**
 * @param {Record<string, unknown>} props
 * @param {string} key
 * @param {{ ignoreHiddenJson?: boolean }} [options]
 */
export function readMultiValueProperty(props, key, options) {
  var baseKey = String(key || '').trim();
  if (!props || typeof props !== 'object' || !baseKey) return [];
  var ignoreHidden = !!(options && options.ignoreHiddenJson);

  if (!ignoreHidden) {
    var fromHidden = readHiddenArray(props, baseKey);
    if (fromHidden.length) return fromHidden;
  }

  var fromNumbered = readNumberedArray(props, baseKey);
  if (fromNumbered.length) return fromNumbered;

  var direct = props[baseKey];
  if (typeof direct === 'string' && direct.indexOf('|||') !== -1) {
    return toCleanArray(direct.split('|||'));
  }
  if (direct == null || direct === '') return [];
  return [String(direct).trim()].filter(Boolean);
}

function mergeSerializedProps(target, key, value, mode) {
  if (isJunkVisibleTier1Property(key, value)) return;
  var serialized = serializePropertyValue(key, value, mode);
  Object.keys(serialized).forEach(function(serializedKey) {
    var serializedVal = serialized[serializedKey];
    if (serializedVal == null || serializedVal === '') return;
    if (isJunkVisibleTier1Property(serializedKey, serializedVal)) return;
    target[serializedKey] = serializedVal;
  });
}

/**
 * @param {Record<string, unknown>} selections
 * @param {Array<{ name?: string }>} [options]
 * @param {'hybrid'|'numbered'|'delimited'} [serializeMode]
 */
export function buildItemPropertiesV2(selections, options, serializeMode) {
  var props = {};
  var skip = new Set();
  (options || []).forEach(function(opt) {
    if (opt && opt.name) skip.add(opt.name);
  });
  skip.add('Title');
  var mode = serializeMode || 'hybrid';

  Object.keys(selections || {}).forEach(function(key) {
    if (skip.has(key)) return;
    if (isSystemPropertyKey(key)) return;
    var val = selections[key];
    if (val == null || val === '') return;

    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.keys(val).forEach(function(subKey) {
        if (isSystemPropertyKey(subKey)) return;
        var subVal = val[subKey];
        if (subVal == null || subVal === '') return;
        mergeSerializedProps(props, subKey, subVal, mode);
      });
      return;
    }

    mergeSerializedProps(props, key, val, mode);
  });

  return props;
}

/** Same as buildItemPropertiesV2 but does not skip variant option names. */
export function buildBundleEffectiveMainPropsV2(selections, options, serializeMode) {
  var props = {};
  var skip = new Set();
  skip.add('Title');
  var mode = serializeMode || 'hybrid';

  Object.keys(selections || {}).forEach(function(key) {
    if (skip.has(key)) return;
    if (isSystemPropertyKey(key)) return;
    var val = selections[key];
    if (val == null || val === '') return;

    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.keys(val).forEach(function(subKey) {
        if (isSystemPropertyKey(subKey)) return;
        var subVal = val[subKey];
        if (subVal == null || subVal === '') return;
        mergeSerializedProps(props, subKey, subVal, mode);
      });
      return;
    }

    mergeSerializedProps(props, key, val, mode);
  });

  return props;
}

/**
 * @param {Record<string, unknown>} props
 * @param {{ ignoreHiddenJson?: boolean }} [options]
 */
export function expandPropertiesV2(props, options) {
  if (!props || typeof props !== 'object') return props || {};

  var out = {};
  var consumed = {};
  var readOpts = options || {};

  Object.keys(props).forEach(function(key) {
    var hiddenBase = parseHiddenJsonKey(key);
    if (hiddenBase && !readOpts.ignoreHiddenJson) consumed[hiddenBase] = true;

    var numbered = parseNumberedKey(key);
    if (numbered && numbered.baseKey) consumed[numbered.baseKey] = true;

    var rawVal = props[key];
    if (typeof rawVal === 'string' && rawVal.indexOf('|||') !== -1) {
      consumed[key] = true;
    }
  });

  Object.keys(consumed).forEach(function(baseKey) {
    var list = readMultiValueProperty(props, baseKey, readOpts);
    if (!list.length) return;
    out[baseKey] = list.length === 1 ? list[0] : dedupePreserveOrder(list);
  });

  Object.keys(props).forEach(function(key) {
    if (parseHiddenJsonKey(key)) return;
    if (parseNumberedKey(key)) return;
    if (consumed[key]) return;
    var val = props[key];
    if (val == null || val === '') return;
    out[key] = val;
  });

  return out;
}

export function collapsePropertiesV2(props) {
  if (!props || typeof props !== 'object') return props || {};

  var rest = {};
  var baseKeys = {};

  Object.keys(props).forEach(function(key) {
    var hiddenBase = parseHiddenJsonKey(key);
    if (hiddenBase) {
      baseKeys[hiddenBase] = true;
      return;
    }

    var numbered = parseNumberedKey(key);
    if (numbered && numbered.baseKey) {
      baseKeys[numbered.baseKey] = true;
      return;
    }

    rest[key] = props[key];
  });

  Object.keys(baseKeys).forEach(function(baseKey) {
    var list = readMultiValueProperty(props, baseKey);
    if (!list.length) return;
    rest[baseKey] = list.length === 1 ? list[0] : list;
  });

  return rest;
}

export const expandDelimiterProperties = expandPropertiesV2;
export const collapseNumberedProperties = collapsePropertiesV2;

function collectVariantOptionNames(options) {
  var names = [];
  (options || []).forEach(function(opt) {
    if (opt && opt.name) names.push(opt.name);
  });
  return names;
}

function stripDesignLibraryHiddenKeys(props) {
  var out = {};
  Object.keys(props || {}).forEach(function(key) {
    if (DESIGN_HIDDEN_KEYS.indexOf(key) !== -1) return;
    out[key] = props[key];
  });
  return out;
}

function parsePbJsonBackup(props) {
  if (!props || !props._pb_json) return null;
  try {
    return JSON.parse(String(props._pb_json));
  } catch (e) {
    return null;
  }
}

function settingsToString(raw) {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    if (typeof raw.settings === 'string') return raw.settings;
    var parts = [];
    if (raw.min != null) parts.push('min:' + raw.min);
    if (raw.max != null) parts.push('max:' + raw.max);
    if (raw.required != null) parts.push('required:' + raw.required);
    return parts.join(', ');
  }
  return String(raw);
}

function parseTopicSettings(raw) {
  var lines = String(raw || '').split('\n');
  var rules = {};
  var topics = [];
  var inTopics = false;
  lines.forEach(function(line) {
    var trimmed = String(line || '').trim();
    if (!inTopics) {
      if (trimmed === '') {
        inTopics = true;
        return;
      }
      var match = trimmed.match(/^([a-zA-Z0-9_]+)\s*:\s*(.+)$/);
      if (match) rules[match[1].toLowerCase()] = match[2].trim();
      return;
    }
    if (trimmed !== '') topics.push(trimmed);
  });
  if (!inTopics) {
    return { rules: {}, topics: lines.map(function(l) { return String(l || '').trim(); }).filter(Boolean) };
  }
  return { rules: rules, topics: topics };
}

function resolvePersonalizationTokens(text, ctx) {
  var str = String(text != null ? text : '');
  if (str.indexOf('{') === -1) return str;
  var selections = (ctx && ctx.selections) || {};
  return str.replace(/\{([^}]+)\}/g, function(_match, label) {
    var key = String(label || '').trim();
    if (!key) return '';
    /* PB-SBT-1 — shared seam so bundle-scoped keys resolve here too. */
    var val = lookupSelectionValue(selections, key);
    if (val == null) return '';
    return String(val);
  });
}

function audioPropertyLabel(textLabel) {
  return String(textLabel || '').trim() + ' (audio)';
}

function topicHasSavedAnswer(ctx, topicTemplate) {
  if (!ctx || !topicTemplate) return false;
  var resolved = resolvePersonalizationTokens(topicTemplate, ctx);
  if (!resolved) return false;
  var text = ctx.selections && ctx.selections[resolved];
  var audio = ctx.selections && ctx.selections[audioPropertyLabel(resolved)];
  var hasText = text != null && String(text).trim() !== '';
  var hasAudio = audio != null && String(audio).trim() !== '';
  return hasText || hasAudio;
}

/**
 * Infer Topic Multi selected topics from per-topic answers on cart line properties.
 * @param {{ selections?: Record<string, unknown>, topicSelections?: Record<string, unknown> }} ctx
 * @param {Array<{ type?: string, label?: string, settings?: unknown }>} fields
 */
export function inferTopicSelectionsFromAnswers(ctx, fields) {
  if (!ctx || !fields || !fields.length) return {};
  ctx.topicSelections = ctx.topicSelections || {};
  fields.forEach(function(field) {
    if (!field || !field.type || String(field.type).indexOf('TopicMulti') === -1 || !field.label) return;
    var fieldLabel = field.label;
    var settingsStr = settingsToString(field.settings);
    var parsed = parseTopicSettings(settingsStr);
    var inferred = parsed.topics.filter(function(topic) {
      return topicHasSavedAnswer(ctx, topic);
    });
    if (!inferred.length) return;
    var existing = Array.isArray(ctx.topicSelections[fieldLabel]) ? ctx.topicSelections[fieldLabel] : [];
    var merged = existing.slice();
    inferred.forEach(function(topic) {
      if (merged.indexOf(topic) === -1) merged.push(topic);
    });
    ctx.topicSelections[fieldLabel] = merged;
  });
  return ctx.topicSelections;
}

/** Stable topic slots carry the chosen question in the key, never in the binding identity. */
export function encodeTopicMultiProperties(state, fields) {
  var result = { properties: {}, consumedKeys: [] };
  var topicFields = (fields || []).filter(f => f && String(f.type).includes('TopicMulti'));
  var ctx = { selections: (state && state.selections) || {}, topicSelections: { ...((state && state.topicSelections) || {}) } };
  inferTopicSelectionsFromAnswers(ctx, topicFields.filter(f => !Array.isArray(ctx.topicSelections[f.label])));
  result.consumedKeys = Array.from(collectTopicMultiAllowedKeys(topicFields, ctx.selections));
  topicFields.forEach(function(field) {
    var selected = ctx.topicSelections[field.label] || [];
    var prefix = topicFields.length > 1 ? field.label + ' ' : '';
    selected.forEach(function(template, index) {
      var question = resolvePersonalizationTokens(template, ctx);
      if (!question) return;
      ['', ' (audio)'].forEach(function(suffix) {
        var rawKey = question + suffix;
        var value = ctx.selections[rawKey];
        result.consumedKeys.push(rawKey);
        if (value == null || String(value).trim() === '') return;
        var slot = prefix + 'Topic ' + (index + 1) + (suffix ? ' audio' : '');
        Object.assign(result.properties, serializePropertyValue(slot + ' — ' + question, value, CART_SERIALIZE_MODE));
      });
    });
  });
  return result;
}

export function decodeTopicMultiProperties(properties, fields, selections, topicSelections, tokenSelections = selections) {
  var topicFields = (fields || []).filter(f => f && String(f.type).includes('TopicMulti'));
  Object.entries(properties || {}).map(function(entry) {
    var match = entry[0].match(/^(.*?)Topic ([1-9][0-9]*)( audio)? — (.+)$/);
    return match ? { match: match, value: entry[1], key: entry[0] } : null;
  }).filter(Boolean).sort((a, b) => Number(a.match[2]) - Number(b.match[2])).forEach(function(entry) {
    var field = topicFields.find(f => entry.match[1] === (topicFields.length > 1 ? f.label + ' ' : ''));
    if (!field) return;
    var question = entry.match[4];
    var template = parseTopicSettings(settingsToString(field.settings)).topics.find(t => resolvePersonalizationTokens(t, { selections: tokenSelections }) === question) || question;
    selections[question + (entry.match[3] ? ' (audio)' : '')] = entry.value;
    delete selections[entry.key];
    var selected = topicSelections[field.label] || (topicSelections[field.label] = []);
    selected[Number(entry.match[2]) - 1] = template;
  });
}

/** @deprecated Use inferTopicSelectionsFromAnswers */
export function inferTopicSelectionsFromAnswersStub(ctx, fields) {
  return inferTopicSelectionsFromAnswers(ctx, fields);
}

function encodeAiTextPropsFromState(state, fieldConfig) {
  var props = {};
  if (!Array.isArray(fieldConfig) || !fieldConfig.length) return props;
  var selections = (state && state.selections) || {};
  var byLabel = state && state.aiTextByLabel && state.aiTextByLabel.byLabel;

  fieldConfig.forEach(function(field) {
    if (!field || !field.label) return;
    if (fieldTypeLower(field).indexOf('ai text') === -1) return;
    var saved = byLabel && byLabel[field.label];
    var runtimeState = saved && typeof saved === 'object' ? saved : null;
    var encoded = encodeAiTextFieldProps(field, runtimeState, selections, state?.currentId);
    Object.keys(encoded).forEach(function(key) {
      props[key] = encoded[key];
    });
  });
  return props;
}

function stripAiTextSelectionProps(props, fieldConfig) {
  if (!props || !Array.isArray(fieldConfig)) return;
  fieldConfig.forEach(function(field) {
    if (!field || !field.label) return;
    if (fieldTypeLower(field).indexOf('ai text') === -1) return;
    var cfg = parseAiTextFieldSettings(field.settings);
    var outputKey = resolveAiTextOutputPropertyKey(cfg, field.label);
    delete props[field.label];
    if (outputKey) {
      delete props[outputKey];
      delete props[`_${outputKey}_ai_meta`];
    }
  });
}

/**
 * Tier 1 encode — human-readable cart/order properties only.
 * @param {{
 *   selections?: Record<string, unknown>,
 *   topicSelections?: Record<string, unknown>,
 *   variantId?: number|string,
 *   catalogDesignLibrary?: { byLabel?: Record<string, unknown> },
 *   aiImageByLabel?: { byLabel?: Record<string, unknown> }
 * }} state
 * @param {Array<{ label?: string, type?: string }>} [fieldConfig]
 * @param {Array<{ name?: string }>} [options]
 */
export function encodeBuilderStateForCart(state, fieldConfig, options) {
  var selections = (state && state.selections) || {};
  var catalogDesignLibrary = state && state.catalogDesignLibrary;
  var aiImageByLabel = state && state.aiImageByLabel;

  if (Array.isArray(fieldConfig) && fieldConfig.length) {
    selections = filterSelectionsForProductFields(selections, fieldConfig, options);
    catalogDesignLibrary = scopeCatalogDesignLibraryForFields(catalogDesignLibrary, fieldConfig);
    selections = stripDesignLibraryTier1KeysFromSelections(selections, fieldConfig);
    if (aiImageByLabel && aiImageByLabel.byLabel) {
      fieldConfig.forEach(function(field) {
        if (!field || !field.label) return;
        if (fieldTypeLower(field).indexOf('ai image') === -1) return;
        var saved = aiImageByLabel.byLabel[field.label];
        if (!saved) return;
        selections = { ...selections };
        mergeAiImageFieldIntoSelections(selections, saved, field);
      });
    }
  }

  var topics = encodeTopicMultiProperties({ ...state, selections: selections }, fieldConfig);
  selections = { ...selections };
  topics.consumedKeys.forEach(key => { delete selections[key]; });
  var props = { ...buildItemPropertiesV2(selections, options, CART_SERIALIZE_MODE), ...topics.properties };
  // Only the AI encoder may write these keys, including when it omits an
  // unavailable result. Ordinary selections are not proof of generation success.
  stripAiTextSelectionProps(props, fieldConfig);
  var aiTextProps = encodeAiTextPropsFromState(
    { currentId: state?.currentId, selections: selections, aiTextByLabel: state && state.aiTextByLabel },
    fieldConfig
  );
  Object.keys(aiTextProps).forEach(function(key) {
    props[key] = aiTextProps[key];
  });
  var designProps = encodeDesignLibraryPropsFromState(
    {
      selections: selections,
      catalogDesignLibrary: catalogDesignLibrary,
    },
    fieldConfig
  );
  var hiddenDesign = {};
  DESIGN_HIDDEN_KEYS.forEach(function(key) {
    if (designProps[key] != null && designProps[key] !== '') {
      hiddenDesign[key] = designProps[key];
    }
  });
  Object.keys(designProps).forEach(function(key) {
    if (DESIGN_HIDDEN_KEYS.indexOf(key) !== -1) return;
    props[key] = designProps[key];
  });

  delete props[DESIGN_SOURCE_KEY];
  delete props[DESIGN_FILE_KEY];

  props = filterVisibleTier1Properties(props);
  Object.keys(hiddenDesign).forEach(function(key) {
    props[key] = hiddenDesign[key];
  });

  return props;
}

function hasLegacyDesignLibraryV1Keys(props) {
  if (!props || typeof props !== 'object') return false;
  var source = props[DESIGN_SOURCE_KEY];
  var file = props[DESIGN_FILE_KEY];
  return (
    (source != null && String(source).trim() !== '') ||
    (file != null && String(file).trim() !== '')
  );
}

/**
 * Cart-edit save — heal legacy v1 Design Source/File into v2 field-label props;
 * purge junk Photo/Character tier-1 keys. Idempotent when encode already wrote v2.
 *
 * @param {Record<string, unknown>} [props]
 * @param {Array<{ label?: string, type?: string }>} [fieldConfig]
 * @param {{ sourceProps?: Record<string, unknown> }} [options]
 */
export function upgradeLegacyCartLinePropsOnSave(props, fieldConfig, options) {
  var out = purgeInvalidAiMediaTier1Keys(props && typeof props === 'object' ? { ...props } : {});
  var source = options && options.sourceProps && typeof options.sourceProps === 'object'
    ? options.sourceProps
    : {};
  var hiddenDesign = {};

  var needsDesignUpgrade =
    hasLegacyDesignLibraryV1Keys(source) ||
    hasLegacyDesignLibraryV1Keys(out);

  delete out[DESIGN_SOURCE_KEY];
  delete out[DESIGN_FILE_KEY];

  if (Array.isArray(fieldConfig) && fieldConfig.length && needsDesignUpgrade) {
    var decodeInput = {};
    Object.keys(source).forEach(function(key) {
      decodeInput[key] = source[key];
    });
    Object.keys(out).forEach(function(key) {
      decodeInput[key] = out[key];
    });
    var catalog = decodeCatalogDesignLibraryFromProps(decodeInput, fieldConfig);
    if (catalog && catalog.byLabel) {
      var designProps = encodeDesignLibraryPropsFromState(
        { selections: decodeInput, catalogDesignLibrary: catalog },
        fieldConfig
      );
      DESIGN_HIDDEN_KEYS.forEach(function(key) {
        if (designProps[key] != null && designProps[key] !== '') {
          hiddenDesign[key] = designProps[key];
        }
      });
      Object.keys(designProps).forEach(function(key) {
        if (DESIGN_HIDDEN_KEYS.indexOf(key) !== -1) return;
        out[key] = designProps[key];
      });
    }
  }

  delete out[DESIGN_SOURCE_KEY];
  delete out[DESIGN_FILE_KEY];
  out = filterVisibleTier1Properties(out);
  Object.keys(hiddenDesign).forEach(function(key) {
    out[key] = hiddenDesign[key];
  });
  return out;
}

/**
 * Tier 3 decode — rebuild builder state from cart line properties (+ legacy _pb_json tolerance).
 * @param {{ properties?: Record<string, unknown>, variant_id?: number|string }} cartItem
 * @param {Array<{ label?: string, type?: string }>} [fieldConfig]
 * @param {{ options?: Array<{ name?: string }> }} [productConfig]
 */
export function decodeCartLineToBuilderState(cartItem, fieldConfig, productConfig) {
  var props = (cartItem && cartItem.properties) || {};
  var flags = {
    usedLegacyPbJson: false,
    usedCodecFallback: true,
    topicInferenceStub: false,
  };

  var backup = parsePbJsonBackup(props);
  var selections = {};
  var topicSelections = {};

  if (backup && backup.selections && typeof backup.selections === 'object') {
    selections = { ...backup.selections };
    flags.usedLegacyPbJson = true;
  }
  if (backup && backup.topicSelections && typeof backup.topicSelections === 'object') {
    topicSelections = { ...backup.topicSelections };
  }

  var fromLine = expandPropertiesV2(props, { ignoreHiddenJson: true });
  Object.keys(fromLine).forEach(function(key) {
    if (isSystemPropertyKey(key)) return;
    selections[key] = fromLine[key];
  });

  decodeTopicMultiProperties(props, fieldConfig, selections, topicSelections);

  var variantId = cartItem && cartItem.variant_id != null ? cartItem.variant_id : null;
  if (backup && backup.variantId != null && variantId == null) {
    variantId = backup.variantId;
  }

  if (!Object.keys(topicSelections).length) {
    topicSelections = inferTopicSelectionsFromAnswers(
      { selections: selections, topicSelections: {} },
      fieldConfig || []
    );
  }

  var catalogDesignLibrary = decodeCatalogDesignLibraryFromProps(props, fieldConfig || []);

  if (Array.isArray(fieldConfig) && fieldConfig.length) {
    hydrateLegacyNameFromInitialSlots(selections, fieldConfig);
  }

  return {
    selections: selections,
    topicSelections: topicSelections,
    variantId: variantId,
    catalogDesignLibrary: catalogDesignLibrary,
    flags: flags,
  };
}

/** Attach tier-1 encode/decode helpers to a live productBuilder instance. */
export function installPbPropertiesOnBuilder(PB) {
  install(PB);
}

function install(PB) {
  if (!PB || PB._pbPropertiesInstalled) return;
  PB._pbPropertiesInstalled = true;

  PB.CART_SERIALIZE_MODE = CART_SERIALIZE_MODE;
  PB.serializePropertyValue = serializePropertyValue;
  PB.readMultiValueProperty = readMultiValueProperty;
  PB.buildItemPropertiesV2 = buildItemPropertiesV2;
  PB.buildBundleEffectiveMainPropsV2 = buildBundleEffectiveMainPropsV2;
  PB.collapsePropertiesV2 = collapsePropertiesV2;
  PB.expandPropertiesV2 = expandPropertiesV2;
  PB.expandDelimiterProperties = expandDelimiterProperties;
  PB.collapseNumberedProperties = collapseNumberedProperties;
  PB.encodeTopicMultiProperties = encodeTopicMultiProperties;
  PB.decodeTopicMultiProperties = decodeTopicMultiProperties;
  PB.encodeBuilderStateForCart = encodeBuilderStateForCart;
  PB.decodeCartLineToBuilderState = decodeCartLineToBuilderState;
  PB.inferTopicSelectionsFromAnswers = inferTopicSelectionsFromAnswers;
  PB.inferTopicSelectionsFromAnswersStub = inferTopicSelectionsFromAnswersStub;
  PB.scopeCatalogDesignLibraryForFields = scopeCatalogDesignLibraryForFields;
  PB.filterSelectionsForProductFields = filterSelectionsForProductFields;
  PB.stripDesignLibraryTier1KeysFromSelections = stripDesignLibraryTier1KeysFromSelections;
  PB.collectTopicMultiAllowedKeys = collectTopicMultiAllowedKeys;
  PB.collectAiImageAllowedKeys = collectAiImageAllowedKeys;
  PB.collectAiTextAllowedKeys = collectAiTextAllowedKeys;
  PB.encodeDesignLibraryPropsFromState = encodeDesignLibraryPropsFromState;
  PB.isLabelAsValueTier1Property = isLabelAsValueTier1Property;
  PB.isAiMediaTier1Key = isAiMediaTier1Key;
  PB.isValidAiMediaTier1Value = isValidAiMediaTier1Value;
  PB.purgeInvalidAiMediaTier1Keys = purgeInvalidAiMediaTier1Keys;
  PB.isJunkVisibleTier1Property = isJunkVisibleTier1Property;
  PB.filterVisibleTier1Properties = filterVisibleTier1Properties;
  PB.upgradeLegacyCartLinePropsOnSave = upgradeLegacyCartLinePropsOnSave;
  PB.catalogDesignLibraryToTier1Props = catalogDesignLibraryToTier1Props;
  PB.decodeCatalogDesignLibraryFromProps = decodeCatalogDesignLibraryFromProps;
}

function tryInstall() {
  if (typeof window === 'undefined' || !window.productBuilder) return false;
  install(window.productBuilder);
  return true;
}

if (typeof window !== 'undefined') {
  if (!tryInstall()) {
    document.addEventListener('DOMContentLoaded', tryInstall);
    document.addEventListener('builder:ready', tryInstall);
  }
}
